import { type JSX, memo } from 'react';
import { cx } from '@linaria/core';

import { useTranslation } from '@cx-modules/i18n-provider';
import { InfoIcon, QuickBetsIcon, Tooltip, type TooltipOptions } from '@cx-modules/ui';

import { useOddsGridContext } from '../../context/OddsGridContext';
import { useNonFavouritedExchangeCount } from '../../hooks/useNonFavouritedExchangeCount';
import { useNonFavouritedExchangeIds } from '../../hooks/useNonFavouritedExchangeIds';
import { useNonFavouritedSportsBookCount } from '../../hooks/useNonFavouritedSportsBookCount';
import { useNonFavouritedSportsBookIds } from '../../hooks/useNonFavouritedSportsBookIds';
import { useOffersSignUpAllCount } from '../../hooks/useOffersSignUpAllCount';
import { useOffersSpecialAllCount } from '../../hooks/useOffersSpecialAllCount';
import { useSmallBookieLogos } from '../../hooks/useSmallBookieLogos';
import { shallow, useSelector } from '../../providers/StoreProvider';
import { BookmakerCell, BookmakerCellSmallIcons } from '../BookmakerCell';
import { InfoSquareDrifting, InfoSquareShortening } from '../OddsInfo/style';
import { OffersGridCellDesktop } from '../OffersGridCellDesktop';
import { InfoLabel, OddsInfoItem, tooltipStyles } from '../shared-styles';

import {
  BookmakersLabel,
  OffersLegend,
  OffersLegendItem,
  QuickBetText,
  QuickBetTooltipDisclosure,
  QuickBetWrapper,
} from './OddsGridHeader.style';
import { BookmakerLogoColumn, GridHeaderRow, LeftColumn, OffersLabel } from './style';

const BookmakerLogoCell = memo(
  ({
    bookieCode,
    showSignupOffers,
    useIntersectionRef,
  }: {
    bookieCode: string;
    showSignupOffers: boolean;
    useIntersectionRef: boolean;
  }) => {
    const { intersectionRef } = useSmallBookieLogos();

    return (
      <BookmakerLogoColumn>
        <div>{showSignupOffers && <OffersGridCellDesktop bookieCode={bookieCode} />}</div>
        <div ref={useIntersectionRef ? intersectionRef : null}>
          <BookmakerCellSmallIcons bookieCode={bookieCode} show={false} />
          <BookmakerCell bookieCode={bookieCode} show={true} />
        </div>
      </BookmakerLogoColumn>
    );
  },
);

BookmakerLogoCell.displayName = 'BookmakerLogoCell';

export const OddsGridHeader = (): JSX.Element | null => {
  const { t } = useTranslation('label');
  const { isTabletOrDesktop } = useOddsGridContext();

  const { allowOddsClickout, repub } = useSelector(
    store => ({
      allowOddsClickout: store.allowOddsClickout,
      repub: store.repub,
    }),
    shallow,
  );

  const numberOfNonFavouritedSportsbooks = useNonFavouritedSportsBookCount();
  const numberOfNonFavouritedExchanges = useNonFavouritedExchangeCount();
  const nonFavouritedSportsbookIds = useNonFavouritedSportsBookIds();
  const nonFavouritedExchangeIds = useNonFavouritedExchangeIds();
  const signupOffersCount = useOffersSignUpAllCount();
  const specialOffersCount = useOffersSpecialAllCount();

  const showSignupOffers = signupOffersCount > 0 && allowOddsClickout && isTabletOrDesktop;
  const showSpecialOffers = specialOffersCount > 0 && allowOddsClickout && isTabletOrDesktop;

  // If TD is the only non-favourited sportsbook, we want to show "Pool" instead of "Sportsbooks" as the label
  const isTDBookieOnly = numberOfNonFavouritedSportsbooks === 1 && nonFavouritedSportsbookIds?.[0] === 'TD';
  const CX_SPORTSBOOKS = isTDBookieOnly ? 'CX_POOL' : 'CX_SPORTSBOOKS';

  const sportsbooksLabel = t(CX_SPORTSBOOKS, { ns: 'label' });
  const exchangeLabel = t('CX_EXCHANGE', { ns: 'label' });
  const signupOffersLabel = t('CX_ODDS_GRID_SIGNUP_OFFERS', { ns: 'label' });
  const specialOffersLabel = t('CX_ODDS_GRID_SPECIAL_OFFERS', { ns: 'label' });
  const driftingLabel = t('CX_DRIFTING', { ns: 'label' });
  const shorteningLabel = t('CX_SHORTENING', { ns: 'label' });

  const tooltipOptions: Omit<TooltipOptions, 'id'> = {
    placement: 'bottom',
    tooltipClass: cx(tooltipStyles),
  };

  if (!isTabletOrDesktop) return null;

  const nonFavouritedSportsbooksExist = numberOfNonFavouritedSportsbooks > 0;
  const nonFavouritedExchangesExist = numberOfNonFavouritedExchanges > 0;
  const onlyOneNonFavouritedSportsbookExist = numberOfNonFavouritedSportsbooks === 1;

  const bookmakerTitle =
    onlyOneNonFavouritedSportsbookExist && nonFavouritedExchangesExist ? sportsbooksLabel : undefined;
  const bookmakerLabelStyle =
    onlyOneNonFavouritedSportsbookExist && nonFavouritedExchangesExist
      ? { overflow: 'hidden', textOverflow: 'ellipsis' }
      : {};
  const exchangeTitle =
    'Unlike traditional bookmakers where traders set the odds, betting exchanges are a marketplace for bettors to buy (back) and sell (lay) the outcome, trading in real-time against each other.';

  return (
    <>
      <GridHeaderRow style={{ marginBottom: 10 }}>
        <LeftColumn />
        <div />
        {nonFavouritedSportsbooksExist && (
          <BookmakersLabel
            style={{ gridColumn: `span ${numberOfNonFavouritedSportsbooks}`, ...bookmakerLabelStyle }}
            title={bookmakerTitle}
          >
            {sportsbooksLabel}
          </BookmakersLabel>
        )}
        {nonFavouritedExchangesExist && <div />}
        {nonFavouritedExchangesExist && (
          <BookmakersLabel style={{ gridColumn: `span ${numberOfNonFavouritedExchanges}` }}>
            <Tooltip disablePortal options={{ ...tooltipOptions, id: 'exchanges-tooltip' }} title={exchangeTitle}>
              <OddsInfoItem>
                <InfoLabel>{exchangeLabel}</InfoLabel>
                {numberOfNonFavouritedExchanges > 2 && <InfoIcon />}
              </OddsInfoItem>
            </Tooltip>
          </BookmakersLabel>
        )}
      </GridHeaderRow>
      <GridHeaderRow style={{ marginBottom: 2 }}>
        <LeftColumn style={{ alignItems: 'start', display: 'grid', gap: '12px', gridTemplateColumns: 'auto auto' }}>
          {repub === 'OC' ? (
            <QuickBetWrapper>
              <Tooltip
                options={{ ...tooltipOptions, id: 'quickbet-header' }}
                title="QuickBet allows customers to place bets with certain bookmakers without leaving the oddschecker website."
              >
                <QuickBetTooltipDisclosure>
                  <QuickBetsIcon />
                  <QuickBetText className="caption">Quick bet</QuickBetText>
                  <InfoIcon />
                </QuickBetTooltipDisclosure>
              </Tooltip>
            </QuickBetWrapper>
          ) : (
            <OffersLegend className={cx(!allowOddsClickout && 'not-supported')}>
              <OffersLegendItem>
                <OddsInfoItem>
                  <InfoSquareShortening />
                  <InfoLabel>{shorteningLabel}</InfoLabel>
                </OddsInfoItem>
              </OffersLegendItem>
              <OffersLegendItem>
                <OddsInfoItem>
                  <InfoSquareDrifting />
                  <InfoLabel>{driftingLabel}</InfoLabel>
                </OddsInfoItem>
              </OffersLegendItem>
            </OffersLegend>
          )}
          {(showSignupOffers || showSpecialOffers) && (
            <OffersLegend>
              {showSignupOffers && (
                <OffersLegendItem>
                  <OffersLabel>{signupOffersLabel}</OffersLabel>
                </OffersLegendItem>
              )}
              {showSpecialOffers && (
                <OffersLegendItem>
                  <OffersLabel>{specialOffersLabel}</OffersLabel>
                </OffersLegendItem>
              )}
            </OffersLegend>
          )}
        </LeftColumn>
        <div />
        {nonFavouritedSportsbookIds.map((bookieCode, index) => (
          <BookmakerLogoCell
            bookieCode={bookieCode}
            key={bookieCode}
            showSignupOffers={showSignupOffers}
            useIntersectionRef={index === 0}
          />
        ))}
        {nonFavouritedExchangesExist && <div />}
        {nonFavouritedExchangeIds.map(bookieCode => (
          <BookmakerLogoCell
            bookieCode={bookieCode}
            key={bookieCode}
            showSignupOffers={showSignupOffers}
            useIntersectionRef={false}
          />
        ))}
      </GridHeaderRow>
    </>
  );
};
